/**
 * Opens one dashboard or report in the builder from an agency area
 * (#/rollups/<id>, #/reports/<id>). A roll-up is "for" the agency: its cover
 * and file names use the agency's name, as AgencyAnalytics' roll-ups do.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { customApi, type CustomMetric, type CustomView } from '../../custom/custom-api.ts';
import { withCustom } from '../../custom/with-custom.ts';

import type { Branding } from '../../portal/portal-api.ts';
import { agencyApi } from '../../reports/agency-api.ts';
import { Builder } from '../../reports/Builder.tsx';
import {
  reportsApi,
  type Catalogue,
  type Client,
  type Page,
  type ReportDocument,
} from '../../reports/reports-api.ts';
import { go, type AgencyArea } from '../routes.ts';

type Open = { document: ReportDocument; pages: Page[] };

export function DocumentEditor(props: {
  organisationId: string;
  area: AgencyArea;
  documentId: string;
  catalogue: Catalogue | null;
  clients: Client[];
}): ReactNode {
  const { organisationId, documentId } = props;
  const [open, setOpen] = useState<Open | null>(null);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [custom, setCustom] = useState<{ metrics: CustomMetric[]; views: CustomView[] }>({
    metrics: [],
    views: [],
  });
  const clientId = open?.document.business_unit_id ?? null;
  // C6 — a roll-up offers the "All clients" custom metrics; a client's document its own too, and its views.
  useEffect(() => {
    if (!open) return;
    Promise.all([
      customApi.metrics(organisationId, clientId),
      clientId ? customApi.views(organisationId, clientId) : Promise.resolve([]),
    ])
      .then(([metrics, views]) =>
        setCustom({
          metrics: clientId ? metrics : metrics.filter((metric) => !metric.business_unit_id),
          views,
        }),
      )
      .catch(() => setCustom({ metrics: [], views: [] }));
  }, [organisationId, clientId, open]);
  const catalogue = useMemo(
    () => (props.catalogue ? withCustom(props.catalogue, custom.metrics, custom.views) : null),
    [props.catalogue, custom],
  );
  useEffect(() => {
    setOpen(null);
    Promise.all([reportsApi.get(organisationId, documentId), agencyApi.branding(organisationId)])
      .then(([document, agency]) => {
        setOpen(document);
        setBranding(agency);
      })
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'This could not be opened.'),
      );
  }, [organisationId, documentId]);

  if (problem)
    return (
      <p className="ag-loading" role="alert">
        {problem} <a href={`#/${props.area}`}>Back</a>
      </p>
    );
  if (!open || !branding || !catalogue) return <p className="ag-loading">Loading…</p>;
  const client = props.clients.find((item) => item.id === open.document.business_unit_id);
  return (
    <Builder
      key={open.document.id}
      organisationId={organisationId}
      catalogue={catalogue}
      initial={open}
      client={client ?? { name: branding.display_name, website: null }}
      branding={branding}
      onClose={() => go({ area: props.area })}
      onOpenDocument={(id) => go({ area: props.area, documentId: id })}
    />
  );
}
